import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell.js';
import { PaymentSheet } from '../../components/payment/PaymentSheet.js';
import { useDailyBrief } from '../../hooks/useDailyBrief.js';
import { useBriefTopicDeepDive } from '../../hooks/useBriefTopicDeepDive.js';
import { useSkuInvoice } from '../../hooks/useSkuInvoice.js';
import { RichText } from '../../components/common/RichText.js';
import styles from './DailyBrief.module.css';

interface PayState {
  params: { idx: number; localDate: string; lang: string; tone: string };
  stars: number;
}

export function DailyBrief(): JSX.Element {
  const { t } = useTranslation(['home', 'common']);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useDailyBrief();
  const deepDive = useBriefTopicDeepDive();
  const { purchase, redeemCoupon } = useSkuInvoice();
  const qc = useQueryClient();
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [cache, setCache] = useState<Map<number, string>>(new Map());
  const [refundedIdx, setRefundedIdx] = useState<Set<number>>(new Set());
  const [pay, setPay] = useState<PayState | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [freeSpent, setFreeSpent] = useState(false);

  useEffect(() => {
    if (deepDive.data?.kind === 'text' && activeIdx != null && !cache.has(activeIdx)) {
      const text = (deepDive.data as { kind: 'text'; text: string }).text;
      setCache((prev) => new Map(prev).set(activeIdx, text));
      setFreeSpent(true);
      qc.invalidateQueries({ queryKey: ['daily-brief'] });
    }
    if (deepDive.data?.kind === 'paywall' && activeIdx != null) {
      setPay({ params: deepDive.data.params, stars: deepDive.data.stars });
      deepDive.reset();
    }
  }, [deepDive.data, activeIdx]);

  const handleTopicClick = (idx: number): void => {
    if (deepDive.isPending) return;
    if (activeIdx === idx) {
      setActiveIdx(null);
      return;
    }
    setActiveIdx(idx);
    if (cache.has(idx)) {
      deepDive.reset();
      return;
    }
    deepDive.reset();
    deepDive.mutate(idx);
  };

  const storeResult = (idx: number, text: string): void => {
    setCache((prev) => new Map(prev).set(idx, text));
  };

  const markRefunded = (idx: number): void => {
    setRefundedIdx((prev) => new Set(prev).add(idx));
  };

  async function doPay(): Promise<void> {
    if (!pay) return;
    setIsPaying(true);
    try {
      const { status } = await purchase('brief_topic', { ...pay.params, src: 'miniapp' });
      if (status === 'paid') {
        const r = await deepDive.pollResult(pay.params.idx, pay.params.localDate);
        if ('error' in r) {
          markRefunded(pay.params.idx);
        } else {
          storeResult(pay.params.idx, r.text);
        }
        setPay(null);
      }
    } catch {
      // payment failed or was cancelled — leave sheet open for retry
    } finally {
      setIsPaying(false);
    }
  }

  async function doCoupon(code: string): Promise<void> {
    if (!pay) return;
    const r = await redeemCoupon('brief_topic', code, { ...pay.params, src: 'miniapp' });
    if (r.text) {
      storeResult(pay.params.idx, r.text);
    } else {
      // coupon redeemed but text not returned inline — poll for it
      const polled = await deepDive.pollResult(pay.params.idx, pay.params.localDate);
      if ('error' in polled) {
        markRefunded(pay.params.idx);
      } else {
        storeResult(pay.params.idx, polled.text);
      }
    }
    setPay(null);
  }

  return (
    <MeSubpageShell title={t('brief.kicker')} data-testid="daily-brief-page">
      <div className={styles.body}>
        {isLoading && <p data-testid="daily-brief-loading">{t('common:states.loading')}</p>}
        {isError && <p role="alert" data-testid="daily-brief-error">{t('common:errors.generic')}</p>}
        {!isLoading && !isError && data?.text == null && (
          <p className={styles.empty} data-testid="daily-brief-empty">{t('brief.empty')}</p>
        )}
        {!isLoading && !isError && data?.text != null && (
          <>
            <RichText className={styles.text} data-testid="daily-brief-text" text={data.text} />
            <button
              type="button"
              className="mt-3 rounded-full bg-blue-900 text-white px-4 py-2"
              data-testid="daily-brief-discuss"
              onClick={() => navigate(`/chat?surface=brief&seedRef=brief:${new Date().toISOString().slice(0, 10)}`)}
            >
              {t('brief.discussBtn', { ns: 'home' })}
            </button>
            {data.topics.length > 0 && (() => {
              const noFreeLeft = (data.freeRemaining ?? 0) <= 0 || freeSpent;
              return (
              <>
                <p className={styles.topicsTitle}>{t('brief.topicsTitle')}</p>
                <div className={styles.topics}>
                  {data.topics.map((topic, i) => (
                    <div key={i} className={styles.topicBlock}>
                      <button
                        type="button"
                        className={`${styles.topic}${activeIdx === i ? ` ${styles.topicActive}` : ''}`}
                        data-testid={`daily-brief-topic-${i}`}
                        onClick={() => handleTopicClick(i)}
                        disabled={deepDive.isPending && activeIdx !== i}
                      >
                        {topic.label}
                        {deepDive.isPending && activeIdx === i && (
                          <span className={styles.topicSpinner} aria-hidden="true" />
                        )}
                        {noFreeLeft && !(deepDive.isPending && activeIdx === i) && (
                          <span className={styles.topicPrice} data-testid="daily-brief-topic-price">{data.topicStars}★</span>
                        )}
                      </button>
                      {activeIdx === i && cache.has(i) && (
                        <RichText className={styles.deepDiveText} data-testid={`daily-brief-deepdive-${i}`} text={cache.get(i)!} />
                      )}
                      {activeIdx === i && refundedIdx.has(i) && (
                        <p className={styles.deepDiveError} role="alert" data-testid={`daily-brief-refunded-${i}`}>
                          {t('brief.deepDiveRefunded', { ns: 'home', defaultValue: 'Stars were refunded — generation failed.' })}
                        </p>
                      )}
                      {activeIdx === i && deepDive.isError && (
                        <p className={styles.deepDiveError} role="alert" data-testid="daily-brief-deepdive-error">
                          {t('common:errors.generic')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </>
              );
            })()}
          </>
        )}
      </div>

      {pay != null && (
        <PaymentSheet
          skuCode="brief_topic"
          title={t('brief.deepDiveBuy', { ns: 'home', defaultValue: 'Unlock deep dive' })}
          priceStars={pay.stars}
          bullets={[]}
          isPaying={isPaying}
          onPay={() => void doPay()}
          onCancel={() => setPay(null)}
          onApplyCoupon={doCoupon}
        />
      )}
    </MeSubpageShell>
  );
}
